import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Activity, ArrowLeft, ChevronRight, ChevronDown, LogOut, Loader2, Plus, RefreshCw,
  Trash2, Send, XCircle, Building2, Users, Copy, Sparkles, MessageSquarePlus,
  UserPlus, Crown, ListPlus,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const VERTICALS = [
  { value: "law_enforcement", label: "Law Enforcement" },
  { value: "fire_service", label: "Fire Service" },
  { value: "ems", label: "EMS" },
  { value: "dispatch_psap", label: "Dispatch / PSAP" },
  { value: "corrections", label: "Corrections" },
  { value: "school_safety", label: "School Safety" },
  { value: "military", label: "Military" },
  { value: "emergency_medicine", label: "Emergency Medicine" },
];

const STATUS_STYLE = {
  active:   "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
  expired:  "bg-amber-500/15  text-amber-500  border-amber-500/40",
  revoked:  "bg-red-500/15    text-red-500    border-red-500/40",
  unknown:  "bg-muted         text-muted-foreground border-border",
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

export default function PlatformDemos() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    vertical: "law_enforcement",
    agency_display_name: "",
    prospect_name: "",
    prospect_email: "",
    personal_note: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/demos");
      setDemos(r.data);
    } catch {
      toast.error("Could not load demos.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await api.post("/platform/demos", form);
      toast.success(r.data.invite_delivered
        ? `Demo created — invite emailed to ${form.prospect_email}`
        : "Demo created — invite email failed to send, copy the link below");
      setShowCreate(false);
      setForm({ vertical: "law_enforcement", agency_display_name: "",
                prospect_name: "", prospect_email: "", personal_note: "" });
      await load();
      if (!r.data.invite_delivered && r.data.invite_link) {
        navigator.clipboard?.writeText(r.data.invite_link).catch(() => {});
        toast.info("Invite link copied to clipboard.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create demo.";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const resend = async (id) => {
    try {
      const r = await api.post(`/platform/demos/${id}/resend`);
      toast.success(r.data.delivered ? "Fresh invite sent." : "Invite email failed — link copied");
      if (!r.data.delivered && r.data.invite_link) {
        navigator.clipboard?.writeText(r.data.invite_link).catch(() => {});
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed."); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this demo? The prospect can no longer sign in.")) return;
    try { await api.post(`/platform/demos/${id}/revoke`); toast.success("Demo revoked."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed."); }
  };
  const hardDelete = async (id) => {
    if (!window.confirm("Delete this demo AND all synthetic data? Cannot be undone.")) return;
    try { await api.delete(`/platform/demos/${id}`); toast.success("Demo deleted."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };
  const editNote = async (d) => {
    const next = window.prompt(
      "Personal note (240 chars max, shown above tour step 1):",
      d.personal_note || "");
    if (next === null) return;  // cancelled
    try {
      await api.patch(`/platform/demos/${d.demo_id}`, { personal_note: next });
      toast.success(next.trim() ? "Note updated." : "Note cleared.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  // --- Multi-contact demos ---
  const [expandedDemo, setExpandedDemo] = useState(null);
  const [contactsByDemo, setContactsByDemo] = useState({});
  const [contactForm, setContactForm] = useState({ name: "", email: "", title: "" });
  const [addingContact, setAddingContact] = useState(false);

  const loadContacts = async (demoId) => {
    try {
      const r = await api.get(`/platform/demos/${demoId}/contacts`);
      setContactsByDemo(prev => ({ ...prev, [demoId]: r.data }));
    } catch { toast.error("Could not load contacts."); }
  };

  const toggleContacts = async (demoId) => {
    if (expandedDemo === demoId) { setExpandedDemo(null); return; }
    setExpandedDemo(demoId);
    setContactForm({ name: "", email: "", title: "" });
    if (!contactsByDemo[demoId]) await loadContacts(demoId);
  };

  const addContact = async (demoId, e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim()) return;
    setAddingContact(true);
    try {
      const r = await api.post(`/platform/demos/${demoId}/contacts`, contactForm);
      toast.success(r.data.invite_delivered
        ? `Invite emailed to ${contactForm.email}`
        : "Contact added — email failed; link copied");
      if (!r.data.invite_delivered && r.data.invite_link) {
        navigator.clipboard?.writeText(r.data.invite_link).catch(() => {});
      }
      setContactForm({ name: "", email: "", title: "" });
      await loadContacts(demoId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add contact.");
    } finally { setAddingContact(false); }
  };

  const resendContact = async (demoId, contactId) => {
    try {
      const r = await api.post(`/platform/demos/${demoId}/contacts/${contactId}/resend`);
      toast.success(r.data.delivered ? "Fresh invite sent." : "Email failed — link copied");
      if (!r.data.delivered && r.data.invite_link) {
        navigator.clipboard?.writeText(r.data.invite_link).catch(() => {});
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed."); }
  };
  const removeContact = async (demoId, contactId) => {
    if (!window.confirm("Remove this contact from the demo?")) return;
    try {
      await api.delete(`/platform/demos/${demoId}/contacts/${contactId}`);
      toast.success("Contact removed.");
      await loadContacts(demoId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed."); }
  };

  // Bulk paste-a-list import
  const [bulkOpenFor, setBulkOpenFor] = useState(null);
  const [bulkRaw, setBulkRaw] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const openBulk = (demoId) => { setBulkOpenFor(demoId); setBulkRaw(""); };
  const closeBulk = () => { setBulkOpenFor(null); setBulkRaw(""); };
  const submitBulk = async (e) => {
    e.preventDefault();
    if (!bulkRaw.trim()) return;
    setBulkSubmitting(true);
    try {
      const r = await api.post(`/platform/demos/${bulkOpenFor}/contacts/bulk`, { raw: bulkRaw });
      const added = r.data.added?.length || 0;
      const skipped = r.data.skipped?.length || 0;
      if (added && !skipped) toast.success(`Added ${added} contact${added === 1 ? "" : "s"} — invites sent.`);
      else if (added && skipped) toast.success(`Added ${added}, skipped ${skipped}.`);
      else toast.error(`No contacts added${skipped ? ` — ${skipped} already existed` : ""}.`);
      await loadContacts(bulkOpenFor);
      closeBulk();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk import failed.");
    } finally { setBulkSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            <div className="rounded-md p-1.5" style={{ background: "hsl(var(--haruki-teal))" }}>
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="font-heading text-base font-bold tracking-tight">Haruki Health</div>
              <div className="text-[10px] uppercase tracking-widest" style={{ color: "hsl(var(--haruki-teal))" }}>
                Admin · Prospect Demos
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/platform" className="text-xs text-muted-foreground hover:text-foreground" data-testid="demos-back-platform">
              <ArrowLeft className="mr-1 inline h-3 w-3" />Platform overview
            </Link>
            <span className="hidden text-xs text-muted-foreground sm:inline">{user?.email}</span>
            <Button data-testid="demos-logout-button" variant="ghost" size="sm"
                    onClick={async () => { await logout(); nav("/"); }}>
              <LogOut className="mr-2 h-3 w-3" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8 lg:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold">Prospect Demos</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Vertical-specific demo workspaces with 20 synthetic employees, populated
              training completions, HMDE scoring, and a recent critical-incident push.
              Each demo lives for 14 days.
            </p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="demos-refresh-button" variant="outline" size="sm" onClick={load}>
              <RefreshCw className="mr-1.5 h-3 w-3" /> Refresh
            </Button>
            <Button data-testid="demos-create-open" size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="mr-1.5 h-3 w-3" /> Create demo
            </Button>
          </div>
        </div>

        {showCreate && (
          <form onSubmit={submit} data-testid="demos-create-form"
                className="mt-6 rounded-lg border border-border bg-card p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="vertical">Vertical</Label>
                <select id="vertical" data-testid="demos-form-vertical" required
                        value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })}
                        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                  {VERTICALS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <Label htmlFor="agency">Agency display name</Label>
                <Input id="agency" data-testid="demos-form-agency" required
                       value={form.agency_display_name}
                       placeholder="FDNY Demo"
                       onChange={(e) => setForm({ ...form, agency_display_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="prospect">Prospect name</Label>
                <Input id="prospect" data-testid="demos-form-prospect-name" required
                       value={form.prospect_name}
                       placeholder="Chief Rodriguez"
                       onChange={(e) => setForm({ ...form, prospect_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="email">Prospect email</Label>
                <Input id="email" data-testid="demos-form-prospect-email" type="email" required
                       value={form.prospect_email}
                       placeholder="chief@example.org"
                       onChange={(e) => setForm({ ...form, prospect_email: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="note">
                  Personal note <span className="text-muted-foreground">(optional, shown above step 1 of their tour)</span>
                </Label>
                <textarea id="note" data-testid="demos-form-note"
                          maxLength={240} rows={2}
                          value={form.personal_note}
                          placeholder='e.g. "Chief Smith — built this after our call. Focus on the SOS step."'
                          onChange={(e) => setForm({ ...form, personal_note: e.target.value })}
                          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
                <div className="mt-1 text-[11px] text-muted-foreground">
                  {form.personal_note.length} / 240
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button data-testid="demos-form-submit" type="submit" disabled={creating}>
                {creating ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Plus className="mr-2 h-3 w-3" />}
                Build demo &amp; email invite
              </Button>
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}
                      data-testid="demos-form-cancel">Cancel</Button>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Data build takes ~2 seconds. A single-use magic link (60-minute TTL) is emailed
              to the prospect. The workspace itself stays active for 14 days.
            </p>
          </form>
        )}

        {loading ? (
          <div className="mt-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading demos…
          </div>
        ) : demos.length === 0 ? (
          <div className="mt-10 rounded-lg border border-dashed border-border p-10 text-center">
            <Sparkles className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
            <div className="font-heading text-lg">No demos yet</div>
            <p className="mt-1 text-sm text-muted-foreground">Create your first vertical-specific demo above.</p>
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-lg border border-border" data-testid="demos-list">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-muted/30 text-[11px] uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Agency</th>
                  <th className="px-4 py-3">Vertical</th>
                  <th className="px-4 py-3">Prospect</th>
                  <th className="px-4 py-3">Employees</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {demos.map((d) => {
                  const dLeft = daysUntil(d.expires_at);
                  const style = STATUS_STYLE[d.status] || STATUS_STYLE.unknown;
                  const label = VERTICALS.find(v => v.value === d.vertical)?.label || d.vertical;
                  const expanded = expandedDemo === d.demo_id;
                  const contacts = contactsByDemo[d.demo_id] || [];
                  const contactCount = contacts.length || 1; // primary always present
                  return (
                    <React.Fragment key={d.demo_id}>
                    <tr data-testid={`demos-row-${d.demo_id}`}
                        className="border-b border-border/60 last:border-0">
                      <td className="px-4 py-3">
                        <button data-testid={`demos-expand-${d.demo_id}`}
                                onClick={() => toggleContacts(d.demo_id)}
                                className="mr-1 inline-flex text-muted-foreground hover:text-foreground align-middle">
                          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                        <span className="font-medium">{d.agency_display_name}</span>
                        <div className="text-[11px] text-muted-foreground font-mono">{d.demo_id}</div>
                      </td>
                      <td className="px-4 py-3">{label}</td>
                      <td className="px-4 py-3">
                        <div>{d.prospect_name}</div>
                        <div className="text-xs text-muted-foreground">{d.prospect_email}</div>
                        {d.personal_note && (
                          <div className="mt-1 max-w-xs truncate text-[11px] italic text-amber-600"
                               title={d.personal_note}
                               data-testid={`demos-note-${d.demo_id}`}>
                            &ldquo;{d.personal_note}&rdquo;
                          </div>
                        )}
                        {expandedDemo === d.demo_id && contacts.length > 1 && (
                          <div className="mt-1 text-[11px] text-muted-foreground">
                            +{contacts.length - 1} additional contact{contacts.length - 1 === 1 ? "" : "s"}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Users className="h-3 w-3" /> {d.employee_count}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-medium ${style}`}>
                          {d.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {d.expires_at ? (
                          <>
                            <div>{new Date(d.expires_at).toLocaleDateString()}</div>
                            {d.status === "active" && dLeft != null && (
                              <div className={dLeft <= 2 ? "text-amber-500" : ""}>{dLeft} d left</div>
                            )}
                          </>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          {d.status === "active" && (
                            <>
                              <Button variant="ghost" size="sm" title="Edit personal note"
                                      data-testid={`demos-edit-note-${d.demo_id}`}
                                      onClick={() => editNote(d)}>
                                <MessageSquarePlus className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="sm" title="Re-send invite"
                                      data-testid={`demos-resend-${d.demo_id}`}
                                      onClick={() => resend(d.demo_id)}>
                                <Send className="h-3 w-3" />
                              </Button>
                              <Button variant="ghost" size="sm" title="Revoke"
                                      data-testid={`demos-revoke-${d.demo_id}`}
                                      onClick={() => revoke(d.demo_id)}>
                                <XCircle className="h-3 w-3 text-amber-500" />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="sm" title="Delete permanently"
                                  data-testid={`demos-delete-${d.demo_id}`}
                                  onClick={() => hardDelete(d.demo_id)}>
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr data-testid={`demos-contacts-${d.demo_id}`}
                          className="border-b border-border/60 bg-muted/20">
                        <td colSpan={7} className="px-4 py-4">
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            Buying committee · {contactCount} contact{contactCount === 1 ? "" : "s"}
                          </div>
                          <div className="mt-2 space-y-1.5">
                            {contacts.map(c => (
                              <div key={c.contact_id} data-testid={`demos-contact-${c.contact_id}`}
                                   className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-xs">
                                {c.is_primary
                                  ? <Crown className="h-3 w-3 text-amber-500" />
                                  : <UserPlus className="h-3 w-3 text-muted-foreground" />}
                                <div className="flex-1">
                                  <span className="font-medium">{c.name}</span>
                                  {c.title && <span className="text-muted-foreground"> · {c.title}</span>}
                                  <span className="ml-2 text-muted-foreground">{c.email}</span>
                                  {c.is_primary && <span className="ml-2 text-[10px] uppercase text-amber-600">Primary</span>}
                                </div>
                                {d.status === "active" && (
                                  <>
                                    <Button variant="ghost" size="sm" title="Re-send invite"
                                            data-testid={`demos-contact-resend-${c.contact_id}`}
                                            onClick={() => resendContact(d.demo_id, c.contact_id)}>
                                      <Send className="h-3 w-3" />
                                    </Button>
                                    {!c.is_primary && (
                                      <Button variant="ghost" size="sm" title="Remove contact"
                                              data-testid={`demos-contact-remove-${c.contact_id}`}
                                              onClick={() => removeContact(d.demo_id, c.contact_id)}>
                                        <XCircle className="h-3 w-3 text-red-500" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                          {d.status === "active" && (
                            <>
                              <form onSubmit={(e) => addContact(d.demo_id, e)}
                                    className="mt-3 grid gap-2 rounded-md border border-dashed border-border bg-card p-3 sm:grid-cols-4"
                                    data-testid={`demos-add-contact-form-${d.demo_id}`}>
                                <Input placeholder="Name" required
                                       data-testid={`demos-contact-name-${d.demo_id}`}
                                       value={contactForm.name}
                                       onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} />
                                <Input placeholder="Email" type="email" required
                                       data-testid={`demos-contact-email-${d.demo_id}`}
                                       value={contactForm.email}
                                       onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
                                <Input placeholder="Title (optional)"
                                       data-testid={`demos-contact-title-${d.demo_id}`}
                                       value={contactForm.title}
                                       onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })} />
                                <Button type="submit" size="sm" disabled={addingContact}
                                        data-testid={`demos-contact-add-${d.demo_id}`}>
                                  {addingContact ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <UserPlus className="mr-1 h-3 w-3" />}
                                  Invite
                                </Button>
                              </form>

                              <div className="mt-2 text-right">
                                <button
                                  type="button"
                                  data-testid={`demos-bulk-open-${d.demo_id}`}
                                  onClick={() => bulkOpenFor === d.demo_id ? closeBulk() : openBulk(d.demo_id)}
                                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                                  <ListPlus className="h-3 w-3" />
                                  {bulkOpenFor === d.demo_id ? "Close paste-a-list" : "Or paste a list of contacts"}
                                </button>
                              </div>
                              {bulkOpenFor === d.demo_id && (
                                <form onSubmit={submitBulk}
                                      data-testid={`demos-bulk-form-${d.demo_id}`}
                                      className="mt-2 rounded-md border border-dashed border-border bg-card p-3">
                                  <label className="text-xs font-medium">Paste one contact per line</label>
                                  <textarea
                                    data-testid={`demos-bulk-textarea-${d.demo_id}`}
                                    rows={5}
                                    value={bulkRaw}
                                    onChange={(e) => setBulkRaw(e.target.value)}
                                    placeholder={`Paste a spreadsheet with a header (Name, Email, Title) — or any of these shapes:

Jane Rivera, jane@fdny.example, Deputy Chief
Tom Bell <tom@fdny.example> · Training Coordinator
lisa.medina@fdny.example`}
                                    className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs" />
                                  <div className="mt-2 flex items-center justify-between">
                                    <div className="text-[11px] text-muted-foreground">
                                      Accepts spreadsheet paste (tab or CSV), <span className="font-mono">Name &lt;email&gt;</span>, or bare emails. Duplicates auto-skipped.
                                    </div>
                                    <div className="flex gap-2">
                                      <Button type="button" variant="ghost" size="sm" onClick={closeBulk}>Cancel</Button>
                                      <Button type="submit" size="sm" disabled={bulkSubmitting || !bulkRaw.trim()}
                                              data-testid={`demos-bulk-submit-${d.demo_id}`}>
                                        {bulkSubmitting
                                          ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                          : <ListPlus className="mr-1 h-3 w-3" />}
                                        Import &amp; invite
                                      </Button>
                                    </div>
                                  </div>
                                </form>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
